'use client';

import { Switch } from '@headlessui/react';
import { Button } from '@ui/core/button';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useState } from 'react';

// Animation variants for card swipe effects
const swipeVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

interface CardStackProps<T> {
  items: T[]; // Array of data items (must not be empty)
  renderItem: (
    item: T,
    index: number, // Original index in the items array
    triggerInteraction: () => void // Callback to signal interaction within the card
  ) => React.ReactNode;
  className?: string; // Optional class for the main container div
  initialIndex?: number; // Default = 0
  navigationArrows?: boolean; // Default = true
  enableExpressMode?: boolean; // Default = false (show toggle if true)
  onIndexChange?: (index: number) => void; // Callback on index change
  onActionInItem?: () => void; // Callback passed FROM renderItem via triggerInteraction
}

function CardStack<T>({
  items,
  renderItem,
  className,
  initialIndex = 0,
  navigationArrows = true,
  enableExpressMode = false,
  onIndexChange,
  onActionInItem,
}: CardStackProps<T>): React.ReactNode {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex ?? 0);
  const [direction, setDirection] = useState<number>(0); // 0=none, 1=next, -1=prev
  const [isExpressModeOn, setIsExpressModeOn] = useState<boolean>(false); // Only used if enableExpressMode is true

  const swipeConfidenceThreshold = 10000;

  // Calculate swipe power based on velocity and offset
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  // Handle the end of a drag gesture
  const handleDragEnd = (offsetX: number, velocityX: number) => {
    const swipe = swipePower(offsetX, velocityX);

    if (swipe > swipeConfidenceThreshold) {
      // Swiped left (next)
      paginate(1);
    } else if (swipe < -swipeConfidenceThreshold) {
      // Swiped right (previous)
      paginate(-1);
    }
  };

  // Change the current index
  const paginate = (newDirection: number) => {
    // Ensure we don't go out of bounds
    const nextIndex = currentIndex + newDirection;

    if (nextIndex >= 0 && nextIndex < items.length) {
      setDirection(newDirection);
      setCurrentIndex(nextIndex);

      // Call the callback if provided
      if (onIndexChange) {
        onIndexChange(nextIndex);
      }
    }
  };

  // Handle interaction within the card (e.g., button click)
  const handleInteraction = () => {
    // Call the callback if provided
    if (onActionInItem) {
      onActionInItem();
    }

    // If express mode is enabled and on, automatically go to the next card
    if (
      enableExpressMode &&
      isExpressModeOn &&
      currentIndex < items.length - 1
    ) {
      paginate(1);
    }
  };

  return (
    <div className={`relative w-full min-h-[200px] ${className || ''}`}>
      {/* Fake background cards for visual stack effect */}
      <div className="absolute inset-0 bg-white rounded-lg shadow-md border border-slate-200/60 transform scale-[0.94] translate-y-2 -z-20" />
      <div className="absolute inset-0 bg-white rounded-lg shadow-md border border-slate-200/60 transform scale-[0.97] translate-y-1 -z-10" />

      {/* Active card with animation */}
      <AnimatePresence initial={false} custom={direction} mode="wait">
        <motion.div
          key={currentIndex}
          className="absolute inset-0 bg-white rounded-lg shadow-sm border border-slate-300/80 p-4 z-0 cursor-grab overflow-hidden flex flex-col"
          custom={direction}
          variants={swipeVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) =>
            handleDragEnd(offset.x, velocity.x)
          }
        >
          {items[currentIndex] &&
            renderItem(items[currentIndex], currentIndex, handleInteraction)}
        </motion.div>
      </AnimatePresence>

      {/* Footer with navigation controls */}
      <div className="flex justify-between items-center pt-4 mt-2 border-t border-slate-200">
        {navigationArrows && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => paginate(-1)}
              disabled={currentIndex === 0}
              className="h-7 px-2"
            >
              <ChevronLeft className="h-3 w-3 mr-1" />
              Previous
            </Button>

            <span className="text-xs text-slate-700">
              Item {currentIndex + 1} of {items.length}
            </span>

            <div className="flex items-center gap-2">
              {enableExpressMode && (
                <div
                  className="flex items-center"
                  title="When enabled, selecting an option will automatically advance to the next item"
                >
                  <Switch
                    checked={isExpressModeOn}
                    onChange={setIsExpressModeOn}
                    className={`${
                      isExpressModeOn ? 'bg-green-700' : 'bg-slate-200'
                    } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-2`}
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        isExpressModeOn ? 'translate-x-5' : 'translate-x-0'
                      } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </Switch>
                  <span className="text-xs ml-1">Express</span>
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => paginate(1)}
                disabled={currentIndex === items.length - 1}
                className="h-7 px-2"
              >
                Next
                <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { CardStack, type CardStackProps };
